import React, { FC, useEffect, useRef, useState } from 'react';
import { Text, View } from 'react-native';
import Header from '@components/settings/v2/common/SettingsHeader';
import { useNavigation, useRoute } from '@react-navigation/native';
import { isPhone, MACHINE_REFERENCE_ACTION, navigateTo } from '@helpers';
import { scale } from '@components/frame';
import i18n from 'i18n-js';
import { font } from '@styles/fonts';
import { connect } from 'react-redux';
import { configureMachineReferences, getMachines, getMachinesReferencesBySourcesSystem } from '@reducers/machines';
import { MachineImnooPerspective, MachinesImnooProps } from '@components/settings/v2/structure/machines/machines/machinesImnoo';
import { PADDING_HORIZONTAL_FOR_SHADOW, SCREEN_PADDING_HORIZONTAL } from '@v2/pages/common/constants/base.constants';
import { ConfigureMachineReference } from '@interfaces/methods';
import RadioButtonsWithFetchCall from '@components/common/radioButtonsWithFetchCall';

interface MachinesImnooDetails extends MachinesImnooProps {
  configureMachineReferences: ConfigureMachineReference;
}

interface MachineNameAndId {
  name: string;
  id?: number;
}

const MachinesImnooDetails: FC<MachinesImnooDetails> = ({
  C,
  getMachines,
  getMachinesReferencesBySourcesSystem,
  machines,
  machinesLoaded,
  machinesReferences,
  machinesReferencesLoaded,
  configureMachineReferences,
}) => {
  const route = useRoute();
  const navigation = useNavigation<any>();
  const machineName = route?.params?.machineName;
  const machinePerspective = route?.params?.machinePerspective;
  const selectedMachine = route?.params?.selectedMachine;
  const machineId = route?.params?.machineId;

  const lastSelectedMachine = useRef<MachineNameAndId>({ name: selectedMachine, id: machineId });
  const [currentSelectedMachine, setCurrentSelectedMachine] = useState<MachineNameAndId | null>({ name: selectedMachine, id: machineId });

  const selectedMachineReference = machinesReferences.find(
    (mr) =>
      mr.name ===
      (machinePerspective === MachineImnooPerspective.IMNOO ? machineName : currentSelectedMachine?.name ?? lastSelectedMachine?.current?.name)
  );

  const onCurrentSelectedMachineChange = async (controller: AbortController) => {
    if (!selectedMachineReference) return;
    const { machines, ...machineReferenceForAPICall } = selectedMachineReference;

    await configureMachineReferences({
      action: !!currentSelectedMachine ? MACHINE_REFERENCE_ACTION.LINK : MACHINE_REFERENCE_ACTION.UNLINK,
      machineId: machinePerspective === MachineImnooPerspective.SF ? machineId : currentSelectedMachine?.id ?? lastSelectedMachine.current?.id,
      machineReference: machineReferenceForAPICall,
      signal: controller.signal,
    });

    const newSelectedMachineName = currentSelectedMachine?.name ?? null;
    navigation?.setParams({ selectedMachine: newSelectedMachineName });
  };

  useEffect(() => {
    if (!machinesLoaded) {
      getMachines();
    }
  }, [machinesLoaded]);

  useEffect(() => {
    getMachinesReferencesBySourcesSystem('Imnoo');
  }, []);

  const onBackButtonPressed = () => {
    navigateTo('Settings', {
      screen: 'Machines',
      params: {
        screen: 'MachinesScreen',
        params: {
          screen: 'MachinesImnoo',
        },
      },
    });
  };

  const loaded = machinesLoaded && (machinesReferencesLoaded || !!machinesReferences.length);
  const updateInProgress = !machinesReferencesLoaded && !!machinesReferences.length;
  const sfPerspective = machinePerspective === MachineImnooPerspective.SF;
  const subtitle = sfPerspective
    ? isPhone
      ? 'SELECT THE IMNOO MACHINE'
      : 'Select the Imnoo Machine'
    : isPhone
    ? 'SELECT THE SMART FACTORY MACHINE'
    : 'Select the Smart Factory Machine';

  const sfMachineNames = machines.map((m) => ({ name: m.shortName, id: m.id }));
  const imnooMachineNames = machinesReferences.map((m) => ({
    name: m.name,
  }));

  const machinesList: MachineNameAndId[] = sfPerspective ? imnooMachineNames : sfMachineNames;

  const onMachineItemPress = (machine: MachineNameAndId) => {
    if (machine.name === currentSelectedMachine?.name) {
      lastSelectedMachine.current = machine;
      setCurrentSelectedMachine(null);
      return;
    }
    setCurrentSelectedMachine(machine);
  };

  const shouldRunTimeout = currentSelectedMachine?.name !== selectedMachine && !!selectedMachineReference;

  return (
    <RadioButtonsWithFetchCall
      checkShouldDisplayLoadingForItem={(machine) => {
        const machineSelected = machine.name === currentSelectedMachine?.name;
        const machineWasUnlinked = !currentSelectedMachine && lastSelectedMachine.current?.name === machine.name;
        return (machineSelected || machineWasUnlinked) && updateInProgress;
      }}
      shouldRunTimeout={shouldRunTimeout}
      loaded={loaded}
      timeoutCallback={onCurrentSelectedMachineChange}
      timeoutDependencies={[currentSelectedMachine?.name]}
      items={machinesList}
      itemKeyExtractor={(machine) => `machine-${machine.name}`}
      isItemSelected={(machine) => machine.name === currentSelectedMachine?.name}
      onItemSelect={(machine) => onMachineItemPress(machine)}
      getItemDisplayText={(machine) => machine.name}
      C={C}
    >
      <Header
        backBtnText={isPhone ? 'Back' : 'Machines'}
        name={'MachinesImnooDetails'}
        header={machineName ?? ' '}
        backButtonAction={onBackButtonPressed}
      />
      <View style={{ marginTop: scale(isPhone ? 0 : 13), paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL) - PADDING_HORIZONTAL_FOR_SHADOW }}>
        <Text
          style={[
            font[isPhone ? 'Footnote' : 'Headline'],
            { color: C(isPhone ? 'Label/Light/Tertiary' : 'Label/Light/Primary'), paddingHorizontal: PADDING_HORIZONTAL_FOR_SHADOW },
          ]}
        >
          {i18n.t(subtitle)}
        </Text>
      </View>
    </RadioButtonsWithFetchCall>
  );
};

export default connect(
  ({ machines: { machinesReferences, machinesReferencesLoaded, machines, machinesLoaded } }) => ({
    machinesReferences,
    machinesReferencesLoaded,
    machines,
    machinesLoaded,
  }),
  {
    getMachinesReferencesBySourcesSystem,
    getMachines,
    configureMachineReferences,
  }
)(MachinesImnooDetails);
